// AudioManager — 包裝 @pixi/sound，依 EventBus 事件播放對應音效與背景音樂（取代 Unity 端的 AudioManager）。
//
// 音效對應皆對照 Unity 第一手呼叫點（SlotStateMachine / ReelController / GamePlayUI / RewardPresentationPanelMediator）：
//   轉輪：se_start（每轉開始）、se_stop（每欄落定/存活符號下墜落定）、se_SS_land_1~3（Scatter 落地，FG 固定 3）、
//         se_draw loop（聽牌 Omen）、se_notice（會中 FG 的開轉預告 ShowPreWin）。
//   中獎：se_win（每步中獎展示）、se_regularwin（底部贏分累加）、se_cascade_1~6（第 N 次連消，>6 不播）、
//         se_symbol_upgrade＋se_wildframe（Wild 變身/金框）、角色 se_character_pistol/bazooka（FG 依等級）、
//         主遊戲中獎語音 se_voice_mgwin_1~3（每轉首次中獎隨機一句）。
//   免費遊戲：se_SS_win（Scatter 展示）、se_fgui（倍率牌/收集面板進場）、se_shift（背景切換）、
//         se_wolf_win＋se_collect（收集）、se_add（獎勵發放）、se_multipler_add（倍率牌遞增）、
//         se_pk_1~4（等級過場）、se_character_upgrade/voice_upgrade_1~4（升級）、mu_free_background_1~4（分級 BGM）。
//   報獎：bw_scoring loop（跑分）→ 逐級 bw_{tier}＋結算語音 → bw_end；MaxWin 為 bw_maxwin＋bw_maxwinloop→bw_maxwin_end。
//         （Unity 引用的 vo_* 語音檔不存在於本作音庫→實際無聲；本移植以 se_voice_total_1~4 對應分級語音。）
//   閒置：se_voice_idle_1~4（角色隨機小動作）、se_wolf（野狼嚎叫）。
//   開幕：se_open（紅幕拉開）→ 布幕播完才 mu_main_background——對照 SlotStateMachine
//         OnUILoadingContinueClickEvent 的 se_open → await PlayOpening() → mu_main_background 順序。
//
// 瀏覽器 autoplay 限制：AudioContext 在使用者手勢前多半是 suspended，這裡在第一次使用者手勢
// （遊戲 loading 的 Continue 按鈕、SPIN 按鈕、或購買鈕）時嘗試 resume 一次。
// ⚠️ 解鎖與「啟動主 BGM」是兩件事：Continue 只解鎖，BGM 等開幕紅幕播完（見 unlockAudioContext/startBgm）。

import { sound } from '@pixi/sound'
import type { EventBus } from '../events/EventBus'
import type { GameEventMap } from '../../game/events/GameEvents'
import type { WinTier } from '../../game/constants'
import { logger } from '../core/Logger'

const SOUND_URLS = {
  spinStart: '/audio/se_start.ogg',
  reelStop: '/audio/se_stop.ogg',
  win: '/audio/se_win.ogg',
  regularWin: '/audio/se_regularwin.ogg',
  freeGamePanel: '/audio/se_fgui.ogg',
  cascade1: '/audio/se_cascade_1.ogg',
  cascade2: '/audio/se_cascade_2.ogg',
  cascade3: '/audio/se_cascade_3.ogg',
  cascade4: '/audio/se_cascade_4.ogg',
  cascade5: '/audio/se_cascade_5.ogg',
  cascade6: '/audio/se_cascade_6.ogg',
  ssLand1: '/audio/se_SS_land_1.ogg',
  ssLand2: '/audio/se_SS_land_2.ogg',
  ssLand3: '/audio/se_SS_land_3.ogg',
  ssWin: '/audio/se_SS_win.ogg',
  omenDraw: '/audio/se_draw.ogg',
  // Free Game 演出音效
  sceneShift: '/audio/se_shift.ogg',
  multiplierAdd: '/audio/se_multipler_add.ogg',
  multiplierBurst: '/audio/se_add.ogg',
  // Scatter 累積計量音效
  scatterCollect: '/audio/se_collect.ogg',
  scatterWin: '/audio/se_wolf_win.ogg',
  scatterBonus: '/audio/se_add.ogg',
  preWinNotice: '/audio/se_notice.ogg',
  wildTransform: '/audio/se_symbol_upgrade.ogg',
  wildFrame: '/audio/se_wildframe.ogg',
  charUpgrade1: '/audio/se_character_upgrade_1.ogg',
  charUpgrade2: '/audio/se_character_upgrade_2.ogg',
  charUpgrade3: '/audio/se_character_upgrade_3.ogg',
  charUpgrade4: '/audio/se_character_upgrade_4.ogg',
  voiceUpgrade1: '/audio/se_voice_upgrade_1.ogg',
  voiceUpgrade2: '/audio/se_voice_upgrade_2.ogg',
  voiceUpgrade3: '/audio/se_voice_upgrade_3.ogg',
  voiceUpgrade4: '/audio/se_voice_upgrade_4.ogg',
  pk1: '/audio/se_pk_1.ogg',
  pk2: '/audio/se_pk_2.ogg',
  pk3: '/audio/se_pk_3.ogg',
  pk4: '/audio/se_pk_4.ogg',
  charPistol: '/audio/se_character_pistol.ogg',
  charBazooka: '/audio/se_character_bazooka.ogg',
  voiceMgWin1: '/audio/se_voice_mgwin_1.ogg',
  voiceMgWin2: '/audio/se_voice_mgwin_2.ogg',
  voiceMgWin3: '/audio/se_voice_mgwin_3.ogg',
  voiceIdle1: '/audio/se_voice_idle_1.ogg',
  voiceIdle2: '/audio/se_voice_idle_2.ogg',
  voiceIdle3: '/audio/se_voice_idle_3.ogg',
  voiceIdle4: '/audio/se_voice_idle_4.ogg',
  wolfHowl: '/audio/se_wolf.ogg',
  // FG 分級背景音樂（升級時切換，對應 Unity mu_free_background_{level}）
  bgmFree2: '/audio/mu_free_background_2.ogg',
  bgmFree3: '/audio/mu_free_background_3.ogg',
  bgmFree4: '/audio/mu_free_background_4.ogg',
  // 報獎（大獎慶祝）音效群
  bwScoring: '/audio/bw_scoring.ogg',
  bwEnd: '/audio/bw_end.ogg',
  bwBig: '/audio/bw_bigwin.ogg',
  bwMega: '/audio/bw_megawin.ogg',
  bwSuper: '/audio/bw_superwin.ogg',
  bwEpic: '/audio/bw_epicwin.ogg',
  bwMax: '/audio/bw_maxwin.ogg',
  bwMaxLoop: '/audio/bw_maxwinloop.ogg',
  bwMaxEnd: '/audio/bw_maxwin_end.ogg',
  // 結算語音（4 級，對應 4 大獎等級）
  voiceTotal1: '/audio/se_voice_total_1.ogg',
  voiceTotal2: '/audio/se_voice_total_2.ogg',
  voiceTotal3: '/audio/se_voice_total_3.ogg',
  voiceTotal4: '/audio/se_voice_total_4.ogg',
  // UI 通用按鈕音（Unity BottomDefine.Se_Button，目前只有押注網格選格會播，對應 BetListWindowManager.OnClickOption）
  uiButton: '/audio/se_button.ogg',
  // 開幕紅幕（SlotStateMachine 於 Continue 點擊後、布幕拉開時播）
  curtainOpen: '/audio/se_open.ogg',
  // 背景音樂（loop）
  bgmMain: '/audio/mu_main_background.ogg',
  bgmFree: '/audio/mu_free_background_1.ogg',
  bgmCutscene: '/audio/mu_cutscene.ogg',
  bgmCongrats: '/audio/mu_congrats.ogg',
} as const

type SoundAlias = keyof typeof SOUND_URLS

/** 依連消次序（1 起算）挑選音檔；Unity 為 `ComboCount<=6` 守衛——第 7 次以後不播（非 clamp 沿用）。 */
const CASCADE_ALIASES: readonly SoundAlias[] = ['cascade1', 'cascade2', 'cascade3', 'cascade4', 'cascade5', 'cascade6']

/** BGM 音軌對照。 */
const BGM_ALIASES: Record<GameEventMap['bgmChanged']['track'], SoundAlias> = {
  main: 'bgmMain',
  free: 'bgmFree',
  cutscene: 'bgmCutscene',
  congrats: 'bgmCongrats',
}

/** 大獎分級 → 報獎音效與結算語音。 */
const TIER_BW: Record<Exclude<WinTier, 'none' | 'max'>, SoundAlias> = {
  big: 'bwBig',
  mega: 'bwMega',
  super: 'bwSuper',
  epic: 'bwEpic',
}
const TIER_VOICE: Record<WinTier, SoundAlias> = {
  none: 'voiceTotal1',
  big: 'voiceTotal1',
  mega: 'voiceTotal2',
  super: 'voiceTotal3',
  epic: 'voiceTotal4',
  max: 'voiceTotal4',
}

/** BGM 音量（壓低以不蓋過音效）。 */
const BGM_VOLUME = 0.35

export class AudioManager {
  private hasUnlockedAudio = false
  /**
   * AudioContext.resume() 完成（可能為 async）。供「必須確定解鎖後才播」的音效等待——
   * 開幕紅幕的 se_open 距離 Continue 點擊只有幾毫秒，不等就可能無聲。
   */
  private audioReady: Promise<void> = Promise.resolve()
  /** 主 BGM 是否已首次啟動（與 hasUnlockedAudio 分開：Unity 是開幕紅幕播完才啟 BGM）。 */
  private bgmStarted = false
  private currentBgm: SoundAlias | null = null
  /** BGM 音量漸變計時器（慶祝淡出/恢復用；null=無進行中漸變）。 */
  private bgmFadeTimer: ReturnType<typeof setInterval> | null = null
  /** 底部贏分上次顯示值：只在「增加」時播 se_regularwin（歸零/回落不播）。 */
  private lastWinShown = 0
  /** 主遊戲中獎語音輪替索引（每次播放換下一句，對應 Unity 隨機語音；用輪替避免 Math.random）。 */
  private mgVoiceIndex = 0
  private idleVoiceIndex = 0

  constructor(private readonly bus: EventBus<GameEventMap>) {
    for (const [alias, url] of Object.entries(SOUND_URLS) as Array<[SoundAlias, string]>) {
      sound.add(alias, url)
    }

    // ── 解鎖（使用者手勢） ──
    this.bus.on('spinRequested', () => this.unlockAudioContext(true))
    // 購買獎勵遊戲可能是玩家的第一個動作（未先按 SPIN）；點擊購買鈕（開確認彈窗）同樣是使用者手勢。
    this.bus.on('buyBonusClicked', () => this.unlockAudioContext(true))
    // 遊戲 loading 的 Continue 按鈕：進入遊戲的第一個使用者手勢，於此解鎖音訊。
    // 主 BGM 不在此啟動——Unity 是開幕紅幕播完才 PlayOneTrackByName("mu_main_background")。
    this.bus.on('audioUnlockRequested', () => this.unlockAudioContext(false))

    // ── 開幕紅幕（Unity SlotStateMachine.OnUILoadingContinueClickEvent）──
    this.bus.on('openingCurtainStarted', () => void this.audioReady.then(() => this.play('curtainOpen')))
    this.bus.on('openingCurtainFinished', () => void this.audioReady.then(() => this.startBgm()))

    // ── 轉輪 ──
    this.bus.on('reelSpinStarted', () => this.play('spinStart'))
    this.bus.on('reelColumnLanded', () => this.play('reelStop'))
    this.bus.on('survivorsDropped', () => this.play('reelStop'))
    this.bus.on('scatterLanded', ({ ordinal }) => {
      const clamped = Math.min(Math.max(ordinal, 1), 3)
      this.play(`ssLand${clamped}` as SoundAlias)
    })
    this.bus.on('anticipationStarted', () => this.playLoop('omenDraw'))
    this.bus.on('anticipationEnded', () => sound.stop('omenDraw'))
    this.bus.on('preWinShown', () => this.play('preWinNotice'))

    // ── 中獎演出 ──
    this.bus.on('stepWinPresented', ({ firstOfSpin, freeGame, fgLevel }) => {
      this.play('win')
      if (freeGame) {
        // FG 角色中獎音：等級 <4 手槍、≥4 火箭筒（對應 Unity PlayCharacterWin）。
        this.play(fgLevel >= 4 ? 'charBazooka' : 'charPistol')
      } else if (firstOfSpin) {
        // 主遊戲每轉首次中獎播一句中獎語音（對應 Unity ResetPlayedVoice 的一轉一次）。
        this.mgVoiceIndex = (this.mgVoiceIndex % 3) + 1
        this.play(`voiceMgWin${this.mgVoiceIndex}` as SoundAlias)
      }
    })
    this.bus.on('winShown', ({ amount }) => {
      if (amount > this.lastWinShown) this.play('regularWin')
      this.lastWinShown = amount
    })
    this.bus.on('tumbleStepResolved', ({ step }) => {
      // Unity `if (ComboCount <= 6)`：第 7 次連消起不播 cascade 音。
      if (step >= 1 && step <= CASCADE_ALIASES.length) this.play(CASCADE_ALIASES[step - 1]!)
    })
    this.bus.on('wildTransformed', () => this.play('wildTransform'))
    this.bus.on('wildFrameShown', () => this.play('wildFrame'))

    // ── 免費遊戲 ──
    this.bus.on('scatterShowcaseShown', () => this.play('ssWin'))
    this.bus.on('sceneShift', () => this.play('sceneShift'))
    this.bus.on('fgPanelsShown', () => this.play('freeGamePanel'))
    this.bus.on('bgmChanged', ({ track }) => this.playBgm(BGM_ALIASES[track]))
    this.bus.on('freeGameMultiplierChanged', ({ animate }) => {
      if (animate) this.play('multiplierAdd')
    })
    this.bus.on('freeGameMainMultiplier', () => this.play('multiplierBurst'))
    this.bus.on('scatterHighlightShown', () => this.play('scatterWin'))
    this.bus.on('scatterCollected', () => this.play('scatterCollect'))
    this.bus.on('scatterLevelUp', ({ level }) => this.handleScatterLevelUp(level))
    this.bus.on('fgLevelIntroShown', ({ level }) => {
      const clamped = Math.min(Math.max(level, 1), 4)
      this.play(`pk${clamped}` as SoundAlias)
    })
    this.bus.on('scatterBonusAwarded', () => this.play('scatterBonus'))
    this.bus.on('freeGameSettleShown', () => this.playBgm('bgmCongrats'))

    // ── 報獎（大獎慶祝；由 RewardOverlayView 依實際演出進度觸發） ──
    // 慶祝期間 BGM 淡出 1s（對應 Unity RewardPresentationPanelMediator.ShowWinCelebration 的
    // AudioManager.FadeBGMVolume(0f, 1f)），結束後恢復原 BGM 音量（PlayOneTrackByName(bgmName, true)）。
    this.bus.on('celebrationStarted', ({ tier }) => {
      this.fadeBgmVolume(0, 1)
      if (tier === 'max') {
        this.play('bwMax')
        this.playLoop('bwMaxLoop')
      } else {
        this.playLoop('bwScoring')
      }
    })
    this.bus.on('celebrationSegment', ({ tier }) => {
      if (tier !== 'none' && tier !== 'max') this.play(TIER_BW[tier])
      this.play(TIER_VOICE[tier])
    })
    this.bus.on('celebrationEnded', ({ tier }) => {
      sound.stop('bwScoring')
      sound.stop('bwMaxLoop')
      this.play(tier === 'max' ? 'bwMaxEnd' : 'bwEnd')
      this.fadeBgmVolume(BGM_VOLUME, 0.5)
    })

    // ── 閒置 ──
    this.bus.on('characterIdle', () => {
      this.idleVoiceIndex = (this.idleVoiceIndex % 4) + 1
      this.play(`voiceIdle${this.idleVoiceIndex}` as SoundAlias)
    })
    this.bus.on('wolfIdle', () => this.play('wolfHowl'))

    // 選單的全域靜音切換（同時靜音音樂與音效，對照 Unity MenuWindowManager 的單一 Volume toggle）。
    this.bus.on('muteToggled', ({ muted }) => (muted ? sound.muteAll() : sound.unmuteAll()))
    // 押注網格選格（對應 BetListWindowManager.OnClickOption 的 AudioManager.PlaySE(Se_Button)）。
    this.bus.on('betSelected', () => this.play('uiButton'))
    // 自動旋轉設定面板的選轉數／勾選（對應 AutoSpinPanel.SetValue／SetOptionActive 的 PlayEffectByName(Se_Button)）。
    this.bus.on('uiButtonPressed', () => this.play('uiButton'))
  }

  /** Scatter 計量升級：播升級音效＋語音，並切到 FG 分級背景音樂（Lv1 進場 bgmFree、Lv2~4 對應軌）。 */
  private handleScatterLevelUp(level: number): void {
    const clamped = Math.min(Math.max(level, 1), 4)
    this.play(`charUpgrade${clamped}` as SoundAlias)
    this.play(`voiceUpgrade${clamped}` as SoundAlias)
    this.playBgm(clamped <= 1 ? 'bgmFree' : (`bgmFree${clamped}` as SoundAlias))
  }

  private play(alias: SoundAlias): void {
    const result = sound.play(alias)
    if (result instanceof Promise) {
      void result.catch((error: unknown) => logger.warn(`音效播放失敗：${alias}`, error))
    }
  }

  /** 播放 loop 音效（若已在播則不重複疊加）。 */
  private playLoop(alias: SoundAlias): void {
    const instance = sound.find(alias)
    if (instance !== undefined && instance.isPlaying) return
    const result = sound.play(alias, { loop: true })
    if (result instanceof Promise) {
      void result.catch((error: unknown) => logger.warn(`音效播放失敗：${alias}`, error))
    }
  }

  /** 切換背景音樂（loop）；同一軌重複請求則忽略，避免打斷。 */
  private playBgm(alias: SoundAlias): void {
    if (this.currentBgm === alias) return
    this.cancelBgmFade()
    if (this.currentBgm !== null) sound.stop(this.currentBgm)
    this.currentBgm = alias
    if (!this.hasUnlockedAudio) return // 尚未解鎖（首次手勢前）不播，解鎖後由 startBgm 補上
    const result = sound.play(alias, { loop: true, volume: BGM_VOLUME })
    if (result instanceof Promise) {
      void result.catch((error: unknown) => logger.warn(`背景音樂播放失敗：${alias}`, error))
    }
  }

  /** 目前 BGM 音量漸變（線性，50ms 步進），對應 Unity AudioManager.FadeBGMVolume。 */
  private fadeBgmVolume(target: number, durationSec: number): void {
    this.cancelBgmFade()
    const alias = this.currentBgm
    if (alias === null) return
    const track = sound.find(alias)
    if (track === undefined) return
    const start = track.volume
    const steps = Math.max(1, Math.round((durationSec * 1000) / 50))
    let step = 0
    this.bgmFadeTimer = setInterval(() => {
      step += 1
      track.volume = start + ((target - start) * step) / steps
      if (step >= steps) this.cancelBgmFade()
    }, 50)
  }

  private cancelBgmFade(): void {
    if (this.bgmFadeTimer !== null) {
      clearInterval(this.bgmFadeTimer)
      this.bgmFadeTimer = null
    }
  }

  /**
   * 只在第一次使用者手勢時嘗試解鎖一次。
   * `startBgm=false` 供 Continue 點擊使用：AudioContext 必須在手勢當下解鎖，但主 BGM 要照 Unity
   * 等開幕紅幕播完（1 秒後的 openingCurtainFinished）才進來，故兩件事拆開。
   */
  private unlockAudioContext(startBgm: boolean): void {
    if (!this.hasUnlockedAudio) {
      this.hasUnlockedAudio = true
      const audioContext = sound.context.audioContext
      this.audioReady =
        audioContext?.state === 'suspended'
          ? audioContext.resume().catch((error: unknown) => logger.warn('AudioContext resume 失敗', error))
          : Promise.resolve()
    }
    if (startBgm) void this.audioReady.then(() => this.startBgm())
  }

  /** 首次啟動背景樂（只做一次）。解鎖前的 playBgm 只會記下 currentBgm，於此補播。 */
  private startBgm(): void {
    if (this.bgmStarted) return
    this.bgmStarted = true
    // 首次啟動時若已有人請求切 BGM 就沿用它，否則預設主遊戲背景樂。
    const target = this.currentBgm ?? 'bgmMain'
    this.currentBgm = null // 清除以讓 playBgm 實際播放
    this.playBgm(target)
  }
}
